import { useEffect, useRef, useState } from "react";
import { Building2, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/frontend/components/ui/field";
import { InviteShell } from "./InviteShell";
import type { InvitationDetails, InviteOnboardingStep } from "@/frontend/types/invite";
import { authClient } from "../../auth-client";
import {
  useAcceptInvitationMutation,
  useConfirmInvitationIdentityMutation,
  usePrepareInviteSignupMutation,
  useRecordInviteActivityMutation,
} from "@/frontend/mutations/invite";
import {
  INVITE_AUTH_FLOW_HEADER,
  INVITE_AUTH_FLOW_SIGNUP,
  clearInviteResumeId,
  clearInviteStateFromLocalStorage,
  getInviteStateFromLocalStorage,
  setInviteStateInLocalStorage,
} from "@/frontend/utils/invite-flow";

const RESEND_COOLDOWN_SECONDS = 60;

export function OnboardingFlow({
  invitationId,
  invitation,
  hasAccount,
}: {
  invitationId: string;
  invitation: InvitationDetails;
  hasAccount: boolean;
}) {
  // Lazy initializers restore step+name from localStorage so tab-switch
  // remounts (e.g. when the user pops over to their inbox after "Send code")
  // don't drop them back to the email/name screen.
  const [name, setName] = useState<string>(() => getInviteStateFromLocalStorage(invitationId).name);
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState<InviteOnboardingStep>(() => {
    const persisted = getInviteStateFromLocalStorage(invitationId).step;
    // Don't restore terminal states — they're stale by the time we remount.
    return persisted === "accepting" || persisted === "success" ? "name-email" : persisted;
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0);
  const otpRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInviteStateInLocalStorage(invitationId, step, name);
  }, [invitationId, step, name]);

  const acceptMutation = useAcceptInvitationMutation();
  const confirmIdentityMutation = useConfirmInvitationIdentityMutation();
  const prepareSignupMutation = usePrepareInviteSignupMutation();
  const recordInviteActivityMutation = useRecordInviteActivityMutation();

  useEffect(() => {
    if (step === "otp") otpRef.current?.focus();
  }, [step]);

  useEffect(() => {
    if (resendSecondsLeft <= 0) return;
    const id = setInterval(() => setResendSecondsLeft((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendSecondsLeft]);

  async function recordActivity(
    activityType:
      | "invite_signup_started"
      | "invite_signup_completed"
      | "invite_signin_completed"
      | "invite_auto_join_succeeded"
      | "invite_auto_join_failed",
    status: "started" | "success" | "error",
    metadata?: Record<string, unknown>,
  ) {
    try {
      await recordInviteActivityMutation.mutateAsync({
        invitationId,
        activityType,
        status,
        flow: "invite-accept",
        metadata,
      });
    } catch {
      // Audit logging must never block invite completion.
    }
  }

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!hasAccount && !name.trim()) {
      setError("Please enter your full name.");
      return;
    }

    setLoading(true);
    try {
      await recordActivity("invite_signup_started", "started");
      // For new users, pre-create the row with the typed name so the OTP
      // verify lands on an existing account with that name. Existing users
      // already have a row — skip.
      if (!hasAccount) {
        await prepareSignupMutation.mutateAsync({
          invitationId,
          name: name.trim(),
        });
      }
      const result = await authClient.emailOtp.sendVerificationOtp({
        email: invitation.email,
        type: "sign-in",
      });
      if (result.error) {
        setError(result.error.message || "Failed to send code.");
        setLoading(false);
        return;
      }
      setStep("otp");
      setResendSecondsLeft(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendSecondsLeft > 0 || loading) return;
    setError(null);
    setLoading(true);
    try {
      const result = await authClient.emailOtp.sendVerificationOtp({
        email: invitation.email,
        type: "sign-in",
      });
      if (result.error) {
        setError(result.error.message || "Failed to resend code.");
      } else {
        setResendSecondsLeft(RESEND_COOLDOWN_SECONDS);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await authClient.signIn.emailOtp(
        // Pass `name` so Better Auth creates the row with the typed name on
        // first verify (disableSignUp:false). For existing users it's
        // ignored. The row is no longer pre-created by prepareInviteSignup —
        // see Codex P2 #3362101839.
        { email: invitation.email, otp, name: hasAccount ? "" : name.trim() },
        { headers: { [INVITE_AUTH_FLOW_HEADER]: INVITE_AUTH_FLOW_SIGNUP } },
      );

      if (result.error) {
        await recordActivity("invite_signin_completed", "error", {
          message: result.error.message,
        });
        setError(result.error.message || "Invalid code.");
        setLoading(false);
        return;
      }
      await recordActivity("invite_signup_completed", "success");

      setStep("accepting");
      await confirmIdentityMutation.mutateAsync({ invitationId });
      await acceptMutation.mutateAsync({ invitationId });
      await recordActivity("invite_auto_join_succeeded", "success");
      clearInviteResumeId(invitationId);
      clearInviteStateFromLocalStorage(invitationId);
      setStep("success");
      setLoading(false);
      setTimeout(() => {
        window.location.href = "/dashboard/patients";
      }, 2000);
    } catch (err) {
      await recordActivity("invite_auto_join_failed", "error", {
        message: err instanceof Error ? err.message : "unknown_error",
      });
      setError(err instanceof Error ? err.message : "An unexpected error occurred.");
      setStep("name-email");
      setLoading(false);
    }
  };

  if (step === "accepting") {
    return (
      <InviteShell>
        <div className="text-center">
          <div className="mx-auto mb-6 flex size-16 items-center justify-center rounded-full bg-primary/10">
            <Building2 className="size-8 text-primary" />
          </div>
          <h1 className="mb-2 text-2xl font-bold">Joining workspace…</h1>
          <p className="text-sm text-muted-foreground">
            Setting up your account in{" "}
            <span className="font-medium text-foreground">{invitation.organizationName}</span>.
          </p>
          <div className="mt-6 flex justify-center">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        </div>
      </InviteShell>
    );
  }

  if (step === "success") {
    return (
      <InviteShell>
        <div className="text-center">
          <div className="mx-auto mb-6 flex size-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30">
            <CheckCircle2 className="size-8 text-emerald-600" />
          </div>
          <h1 className="mb-2 text-2xl font-bold">You're in!</h1>
          <p className="text-sm text-muted-foreground">
            Welcome to{" "}
            <span className="font-medium text-foreground">{invitation.organizationName}</span>.
            Redirecting you to the dashboard…
          </p>
        </div>
      </InviteShell>
    );
  }

  return (
    <InviteShell>
      <form
        onSubmit={step === "name-email" ? handleRequestOtp : handleVerifyAndJoin}
        className="flex flex-col gap-6"
      >
        <FieldGroup>
          <div className="flex flex-col items-center gap-1 text-center mb-2">
            <div className="flex size-12 items-center justify-center rounded-xl bg-primary/10 mb-2">
              <Building2 className="size-6 text-primary" />
            </div>
            <h1 className="text-2xl font-bold">Join {invitation.organizationName}</h1>
            <p className="text-sm text-muted-foreground text-balance">
              You've been invited as a{" "}
              <span className="font-medium text-foreground capitalize">{invitation.role}</span>.
              {step === "name-email" ? (
                hasAccount
                  ? " We'll send a code to confirm it's you."
                  : " Set up your account to get started."
              ) : (
                <> Enter the code we sent to <span data-ph-mask>{invitation.email}</span>.</>
              )}
            </p>
          </div>

          {step === "name-email" && (
            <>
              <Field>
                <FieldLabel htmlFor="invite-email">Email</FieldLabel>
                <Input
                  id="invite-email"
                  type="email"
                  value={invitation.email}
                  readOnly
                  className="bg-muted/50 cursor-not-allowed"
                />
              </Field>

              {!hasAccount && (
                <Field>
                  <FieldLabel htmlFor="invite-name">Full Name</FieldLabel>
                  <Input
                    id="invite-name"
                    type="text"
                    placeholder="Jane Smith"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    autoComplete="name"
                    autoFocus
                  />
                </Field>
              )}
            </>
          )}

          {step === "otp" && (
            <Field>
              <FieldLabel htmlFor="invite-otp">Verification Code</FieldLabel>
              <Input
                ref={otpRef}
                id="invite-otp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                placeholder="Enter 6-digit code"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                required
                autoComplete="one-time-code"
                className="h-11 text-center tracking-widest"
              />
              <FieldDescription className="text-center">
                {resendSecondsLeft > 0 ? (
                  <>Resend code in {resendSecondsLeft}s</>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={loading}
                    className="underline underline-offset-2 hover:text-foreground disabled:opacity-50"
                  >
                    Resend code
                  </button>
                )}
              </FieldDescription>
            </Field>
          )}

          {error && (
            <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <Field>
            <Button type="submit" disabled={loading || (step === "otp" && otp.length !== 6)} className="w-full">
              {loading ? (
                <>
                  <Loader2 className="animate-spin" data-icon="inline-start" />
                  {step === "name-email" ? "Sending code…" : "Joining…"}
                </>
              ) : step === "name-email" ? (
                "Send code"
              ) : (
                "Verify & join"
              )}
            </Button>
          </Field>

          {step === "otp" && (
            <button
              type="button"
              onClick={() => {
                setStep("name-email");
                setOtp("");
                setError(null);
                setResendSecondsLeft(0);
              }}
              className="text-center text-sm text-[#1A73E8] hover:underline"
            >
              Back
            </button>
          )}
        </FieldGroup>
      </form>
    </InviteShell>
  );
}
